import React, { FC, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import loadAIProton from "@NA/Widgets/AIProton/AIProtonHandler";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import AIProtonProductCarousel from "@NA/Widgets/AIProton/AIProtonProductCarousel";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductCardSelections } from "@NA/Widgets/AIProton/AIProtonModel";
import { HasProduct } from "@insite/client-framework/Components/ProductContext";
import { getSelectedProductPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import useLocation from "@NA/Hooks/useLocation";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";

export interface AIProtonStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    carouselWrapperInner?: InjectableCss;
}

const enum fields {
    title = "title",
    titleAlignment = "titleAlignment",
    showImage = "showImage",
    showItemCompare = "showItemCompare",
    showBrand = "showBrand",
    showTitle = "showTitle",
    showPartNumbers = "showPartNumbers",
    showAvailability = "showAvailability",
    showAttributes = "showAttributes",
    showPrice = "showPrice",
    showAddToList = "showAddToList",
    showSavingsAmount = "showSavingsAmount",
    showSavingsPercent = "showSavingsPercent",
    gaPromoCode = "gaPromoCode",
    screenType = "screenType",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titleAlignment]: "left" | "center";
        [fields.showImage]: boolean;
        [fields.showItemCompare]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrand]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showAvailability]: boolean;
        [fields.showAttributes]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToList]: boolean;
        [fields.showSavingsAmount]: boolean;
        [fields.showSavingsPercent]: boolean;
        [fields.gaPromoCode]: string;
        [fields.screenType]: string;
    };
}

type Props = OwnProps & ProductCardSelections & HasProduct;

export const protontyles: AIProtonStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    carouselWrapperInner: {
        css: css`
            margin-bottom: 40px;
            margin-top: 20px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [null, css` margin-bottom: 10px; `,], "max",);
            }}
        `,
    },
};

const styles = protontyles;

const AIProtonView: FC<Props> = ({ fields, product }) => {
    const dispatch: any = useDispatch();
    const currentState = useSelector((state: CustomApplicationState) => state);
    const onLoadRef = useRef(false);
    const overriddenProps = { ...fields };
    const location = useLocation();
    const optionId = location?.search?.split("=")[1];
    const normalProductId = location?.pathname?.split("/");
    const id = normalProductId[2];

    const loadAIProtonProducts = () => {
        const cart = getCurrentCartState(currentState);
        const billToState = getBillToState(currentState, cart?.value?.billToId);
        const productPath = getSelectedProductPath(currentState) || productPathValue(currentState);
        const recommenderProductproduct = getProductStateByPath(currentState, productPath);
        if (billToState.value) {
            onLoadRef.current = true;
            if (overriddenProps.screenType === "FBT PDP") {
                dispatch(
                    loadAIProton({
                        userID: billToState.value?.customerNumber,
                        productID: optionId || id,
                        Scenario: "FBT",
                        widgetName: fields.title,
                        dispatch: "PDP/DispatchCompleteLoadAIProtonFBT",
                    } as any),
                );
            } else if (overriddenProps.screenType === "Similar PDP") {
                dispatch(
                    loadAIProton({
                        userID: billToState.value?.customerNumber,
                        productID: optionId || id,
                        Scenario: "Similar",
                        widgetName: fields.title,
                        dispatcha: "PDP/DispatchCompleteLoadAIProtonSimilar",
                    } as any),
                );
            } else {
                dispatch(
                    loadAIProton({
                        userID: billToState.value?.customerNumber,
                        widgetName: fields.title,
                        dispatch: "Homepage/DispatchCompleteLoadAIProton",
                        productID: "",
                        Scenario: ""
                    } as any),
                );
            }
        }
    };

    const recommederProductValue = () => {
        if (product && product.variantTypeId == null) {
            return product.productNumber;
        }
    };

    const productPathValue = (state: ApplicationState) => {
        const location = getLocation(state);
        if (location.pathname.toLowerCase().startsWith("/content/")) {
            return "";
        }
        return location.pathname;

    };

    useEffect(() => {
        if (!onLoadRef.current) loadAIProtonProducts();
    }, [currentState, location, optionId, id]);

    if (overriddenProps.screenType === "FBT PDP" && !currentState.ADIGlobalData?.AIProtonStore?.productsFBT) {
        return null;
    }

    if (overriddenProps.screenType === "Similar PDP" && !currentState.ADIGlobalData?.AIProtonStore?.productsSimilar) {
        return null;
    }

    if (overriddenProps.screenType === "home" && !currentState.ADIGlobalData?.AIProtonStore?.products) {
        return null;
    }

    let products: any = [];

    if (overriddenProps.screenType === "FBT PDP") {
        products = currentState.ADIGlobalData?.AIProtonStore?.productsFBT;
    } else if (overriddenProps.screenType === "Similar PDP") {
        products = currentState.ADIGlobalData?.AIProtonStore?.productsSimilar;
    } else {
        products = currentState.ADIGlobalData?.AIProtonStore?.products;
    }

    const displayProtonProducts = () => {
        if (currentState.context.session!.isAuthenticated) {
            if (products.length) {
                return (
                    <StyledWrapper {...styles.carouselWrapperInner}>
                        <AIProtonProductCarousel
                            products={products}
                            {...overriddenProps}
                            titleTextAlignment={fields.titleAlignment}
                        />
                    </StyledWrapper>
                );
            }
            return null;

        }
        return null;

    };

    return <>{displayProtonProducts()}</>;
};

const widgetModule: WidgetModule = {
    component: AIProtonView,
    definition: {
        group: "Common",
        displayName: "ADI AI Proton View",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                displayName: "Title",
                defaultValue: "Buy Now",
                fieldType: "General",
                isRequired: true,
                sortOrder: 1,
            },
            {
                name: fields.showItemCompare,
                displayName: "Show Compare",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showBrand,
                displayName: "Show Brand",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showTitle,
                displayName: "Show Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Show Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showAvailability,
                displayName: "Show Availability",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showPrice,
                displayName: "Show Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showAddToList,
                displayName: "Show Add To List",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.showSavingsAmount,
                displayName: "Show Savings Amount",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 10,
            },
            {
                name: fields.showSavingsPercent,
                displayName: "Show Savings Percent",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 11,
            },
            {
                name: fields.showImage,
                displayName: "Show Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 12,
            },
            {
                name: fields.gaPromoCode,
                editorTemplate: "TextField",
                displayName: "GA Promo Tag",
                defaultValue: "Homepage-BuyAgain-ProtonAI",
                fieldType: "General",
                isRequired: false,
                sortOrder: 13,
            },
            {
                name: fields.screenType,
                editorTemplate: "TextField",
                displayName: "Screen Type",
                defaultValue: "home",
                fieldType: "General",
                isRequired: true,
                sortOrder: 14,
            },
            {
                name: fields.titleAlignment,
                displayName: "Title Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                ],
                defaultValue: "left",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
